import { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "@/api";
import SignatureRemindersCard from "./SignatureRemindersCard";
import TaskAlertsCard from "./TaskAlertsCard";
import { toast } from "@/lib/toast";
import ActionRequiredBanner from "./ActionRequiredBanner.jsx";
import {
  Mail, RefreshCw, CheckCircle2, XCircle, Bell, Activity,
  Wifi, WifiOff, Loader2, BellRing, AlertTriangle, Send, Save,
} from "lucide-react";

/**
 * Batch #14.216 — Paramètres de notifications (refonte granulaire).
 *
 * Changements vs Batch #14.88 :
 *  - Pour CHAQUE event, 2 toggles indépendants : 📧 Email + 🔔 Push
 *  - Catalogue d'événements chargé depuis le backend (élargi de 33 → 60+)
 *  - Auto-refresh ne wipe plus les modifications non sauvegardées
 *  - Barre flottante « Modifications non enregistrées » avec gros bouton
 *    Sauvegarder qui suit le scroll
 */

const PERM_LABEL = {
  granted: { txt: "Autorisée", cls: "text-emerald-600", icon: CheckCircle2 },
  denied: { txt: "Refusée", cls: "text-red-600", icon: XCircle },
  default: { txt: "Non demandée", cls: "text-amber-600", icon: AlertTriangle },
  unsupported: { txt: "Non supporté", cls: "text-gray-500", icon: AlertTriangle },
};

function PermBadge({ permission }) {
  const cfg = PERM_LABEL[permission] || PERM_LABEL.unsupported;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs ${cfg.cls}`}>
      <Icon className="w-3.5 h-3.5" /> {cfg.txt}
    </span>
  );
}

// Normalise une valeur de pref event en {email, push} (bool legacy ou dict)
function normEv(v) {
  if (v == null) return { email: true, push: true, bell: true };
  if (typeof v === "boolean") return { email: v, push: v, bell: v };
  return { email: v.email !== false, push: v.push !== false, bell: v.bell !== false };
}

// Comparaison profonde sommaire pour détecter le dirty state
function configEquals(a, b) {
  if (!a || !b) return a === b;
  if (a.cabinet_email !== b.cabinet_email) return false;
  if (!!a.email_enabled !== !!b.email_enabled) return false;
  if (!!a.cabinet_auto_notify !== !!b.cabinet_auto_notify) return false;
  if (JSON.stringify(a.signature_reminders || {}) !== JSON.stringify(b.signature_reminders || {})) return false;
  if (!!a.email_channel_enabled !== !!b.email_channel_enabled) return false;
  if (!!a.push_channel_enabled !== !!b.push_channel_enabled) return false;
  if (!!a.bell_channel_enabled !== !!b.bell_channel_enabled) return false;
  const aK = Object.keys(a.events || {});
  const bK = Object.keys(b.events || {});
  if (aK.length !== bK.length) return false;
  for (const k of new Set([...aK, ...bK])) {
    const av = normEv(a.events?.[k]);
    const bv = normEv(b.events?.[k]);
    if (av.email !== bv.email || av.push !== bv.push || av.bell !== bv.bell) return false;
  }
  return true;
}

export default function NotificationsSection() {
  const [config, setConfig] = useState(null);
  const [savedConfig, setSavedConfig] = useState(null); // dernier état serveur
  const [catalog, setCatalog] = useState([]); // depuis le backend
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const [pushDetail, setPushDetail] = useState(null);
  const [browserStatus, setBrowserStatus] = useState(null);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [testingPush, setTestingPush] = useState(false);
  const [lastTest, setLastTest] = useState(null);

  const dirty = useMemo(() => !configEquals(config, savedConfig), [config, savedConfig]);

  // Charge la config (la PREMIÈRE FOIS uniquement, ou sur demande explicite)
  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const [c, cat] = await Promise.all([
        api.get("/settings/notifications"),
        api.get("/settings/notifications/event-catalog"),
      ]);
      setConfig(c.data);
      setSavedConfig(JSON.parse(JSON.stringify(c.data)));
      setCatalog(cat.data?.groups || []);
    } catch {
      toast.error("Impossible de charger la configuration notifications");
    } finally {
      setLoading(false);
    }
  }, []);

  // Refresh SILENCIEUX du push status seulement (ne touche pas la config events)
  const refreshPushStatus = useCallback(async () => {
    try {
      const p = await api.get("/push/status");
      setPushDetail(p.data);
      // Met à jour uniquement push_status dans config (sans wiper les events)
      setConfig((prev) => prev ? { ...prev, push_status: p.data?.push_status || prev.push_status } : prev);
      setSavedConfig((prev) => prev ? { ...prev, push_status: p.data?.push_status || prev.push_status } : prev);
    } catch { /* silent */ }
    setRefreshing(false);
  }, []);

  const loadBrowserStatus = useCallback(async () => {
    try {
      const { getPushStatus } = await import("@/services/firebase");
      const s = await getPushStatus();
      setBrowserStatus(s);
    } catch (e) {
      setBrowserStatus({ supported: false, initError: String(e) });
    }
  }, []);

  const loadClients = useCallback(async () => {
    try {
      const r = await api.get("/clients");
      setClients(Array.isArray(r.data) ? r.data : (r.data?.items || []));
    } catch {
      setClients([]);
    }
  }, []);

  useEffect(() => {
    loadConfig();
    refreshPushStatus();
    loadBrowserStatus();
    loadClients();
  }, [loadConfig, refreshPushStatus, loadBrowserStatus, loadClients]);

  // Batch #14.216 — Auto-refresh SEULEMENT du push status (jamais des events !)
  useEffect(() => {
    const interval = setInterval(() => { refreshPushStatus(); }, 30000);
    return () => clearInterval(interval);
  }, [refreshPushStatus]);

  const refresh = () => { setRefreshing(true); refreshPushStatus(); };

  const update = (k, v) => setConfig((prev) => ({ ...prev, [k]: v }));

  const setEventChannel = (eventKey, channel, value) => {
    setConfig((prev) => {
      const ev = { ...(prev?.events || {}) };
      const current = normEv(ev[eventKey]);
      ev[eventKey] = { ...current, [channel]: value };
      return { ...prev, events: ev };
    });
  };

  const bulkSetGroup = (groupKeys, channel, value) => {
    setConfig((prev) => {
      const ev = { ...(prev?.events || {}) };
      groupKeys.forEach((k) => {
        const cur = normEv(ev[k]);
        ev[k] = channel === "all"
          ? { email: value, push: value, bell: value }
          : { ...cur, [channel]: value };
      });
      return { ...prev, events: ev };
    });
  };

  const handleSave = async () => {
    if (saving || !config) return;
    setSaving(true);
    try {
      const r = await api.put("/settings/notifications", {
        cabinet_email: config.cabinet_email || null,
        email_enabled: !!config.email_enabled,
        cabinet_auto_notify: !!config.cabinet_auto_notify,
        signature_reminders: config.signature_reminders || undefined,
        email_channel_enabled: config.email_channel_enabled !== false,
        push_channel_enabled: config.push_channel_enabled !== false,
        bell_channel_enabled: config.bell_channel_enabled !== false,
        events: config.events || {},
      });
      setConfig(r.data);
      setSavedConfig(JSON.parse(JSON.stringify(r.data)));
      toast.success("Préférences enregistrées");
      window.dispatchEvent(new CustomEvent("settings:updated", { detail: ["notifications"] }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    if (!savedConfig) return;
    setConfig(JSON.parse(JSON.stringify(savedConfig)));
    toast.message("Modifications annulées");
  };

  const handleSendEmailTest = async () => {
    if (testingEmail) return;
    if (!config?.cabinet_email) {
      toast.error("Configurez d'abord un courriel destinataire");
      return;
    }
    setTestingEmail(true);
    try {
      const r = await api.post("/settings/smtp/test", { to: config.cabinet_email });
      if (r.data?.success) toast.success(`Email de test envoyé à ${config.cabinet_email}`);
      else toast.error(r.data?.message || "Échec du test SMTP");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur réseau");
    } finally {
      setTestingEmail(false);
    }
  };

  const runPushTest = async (clientId = null) => {
    setTestingPush(true);
    setLastTest(null);
    try {
      const payload = { message: clientId ? "Test diagnostic (client)" : "Test diagnostic (admin)" };
      if (clientId) payload.client_id = clientId;
      const r = await api.post("/push/test", payload);
      setLastTest(r.data);
      if (r.data?.fallback_used) {
        toast.message(r.data.message, { description: "Aucun appareil joignable" });
      } else {
        toast.success(r.data.message || `Push envoyé à ${r.data.sent_count} appareil(s)`);
      }
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      setLastTest({ ok: false, error: detail });
      toast.error(detail);
    } finally {
      setTestingPush(false);
      refreshPushStatus();
    }
  };

  const requestBrowserPermission = async () => {
    try {
      const { requestNotificationPermission } = await import("@/services/firebase");
      const result = await requestNotificationPermission();
      if (result?.ok) {
        const reg = await api.post("/push/register", { token: result.token });
        if (reg.data?.ok) toast.success(`Notifications activées (rôle: ${reg.data.role})`);
        else toast.error(`Enregistrement échoué: ${reg.data?.error || "inconnu"}`);
      } else {
        const reasons = {
          "unsupported": "Navigateur ne supporte pas les notifications",
          "messaging-unsupported": "Firebase Messaging non supporté",
          "not-configured": "Configuration Firebase absente (REACT_APP_FIREBASE_*)",
          "permission-denied": "Permission refusée — autorisez dans les réglages du navigateur",
          "permission-error": "Erreur lors de la demande de permission",
          "no-vapid": "Clé VAPID non configurée",
          "no-token": "Impossible d'obtenir un token (vérifiez VAPID + permission)",
          "token-error": `Erreur token: ${result?.error || ""}`,
        };
        toast.error(reasons[result?.reason] || `Échec: ${result?.reason || "inconnu"}`);
      }
      loadBrowserStatus();
      refreshPushStatus();
    } catch (e) {
      toast.error("Erreur: " + (e.message || e));
    }
  };

  if (loading) {
    return (
      <div className="bg-white border rounded-xl p-5 text-center text-gray-400 text-sm" data-testid="notifications-section-loading">
        <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Chargement de la configuration...
      </div>
    );
  }
  if (!config) return null;

  const push = config.push_status || {};
  const events = config.events || {};
  const pushActive = push.firebase_initialized && (push.tokens?.admin || 0) > 0;
  const creds = pushDetail?.creds_diagnostic;

  return (
    <div className="space-y-4 pb-24" data-testid="notifications-section">
      <ActionRequiredBanner sectionKey="notifications" title="Action requise : configurez vos notifications" />

      {/* ── 1. Canaux globaux ─────────────────────────────────────────────── */}
      <div className="bg-white border rounded-xl p-5 space-y-3" data-testid="notif-channels-card">
        <h3 className="font-semibold text-sm flex items-center gap-2">
          <Bell className="w-4 h-4" /> Canaux de réception (globaux)
        </h3>
        <p className="text-xs text-slate-500">
          Coupe-circuit global. Si tu désactives Email ici, AUCUN event n&apos;enverra de courriel, même si l&apos;event a son toggle Email activé.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <label className={`flex items-start gap-3 cursor-pointer rounded-lg border-2 p-3 transition ${
            config?.email_channel_enabled !== false ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-slate-50"
          }`}>
            <input type="checkbox" checked={config?.email_channel_enabled !== false}
              onChange={(e) => setConfig({ ...config, email_channel_enabled: e.target.checked })}
              className="mt-0.5" data-testid="notif-channel-email" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm flex items-center gap-1.5"><span>📧</span> Courriel (global)</div>
              <div className="text-[11px] text-slate-600 mt-0.5">
                Reçois les notifications par email à <code className="text-emerald-700">{config?.cabinet_email || "ton adresse cabinet"}</code>
              </div>
            </div>
          </label>

          <label className={`flex items-start gap-3 cursor-pointer rounded-lg border-2 p-3 transition ${
            config?.push_channel_enabled !== false ? "border-indigo-300 bg-indigo-50" : "border-slate-200 bg-slate-50"
          }`}>
            <input type="checkbox" checked={config?.push_channel_enabled !== false}
              onChange={(e) => setConfig({ ...config, push_channel_enabled: e.target.checked })}
              className="mt-0.5" data-testid="notif-channel-push" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm flex items-center gap-1.5"><span>📱</span> Push (global)</div>
              <div className="text-[11px] text-slate-600 mt-0.5">
                Reçois les notifications instantanées sur ton navigateur / téléphone
              </div>
            </div>
          </label>

          <label className={`flex items-start gap-3 cursor-pointer rounded-lg border-2 p-3 transition ${
            config?.bell_channel_enabled !== false ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-slate-50"
          }`}>
            <input type="checkbox" checked={config?.bell_channel_enabled !== false}
              onChange={(e) => setConfig({ ...config, bell_channel_enabled: e.target.checked })}
              className="mt-0.5" data-testid="notif-channel-bell" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm flex items-center gap-1.5"><span>🔔</span> Cloche (global)</div>
              <div className="text-[11px] text-slate-600 mt-0.5">
                Notifications dans l&apos;application (icône cloche en haut à droite)
              </div>
            </div>
          </label>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-[11px] text-slate-600">
          💡 <strong>Tes propres actions ne te notifient plus.</strong> Modifier une fiche client, envoyer une relance,
          un document, une facture ou une demande de signature ne crée plus de notification — seules les actions de tes
          clients et les événements externes (leads, paiements, SMS reçus…) te notifient.
        </div>
        {config?.email_channel_enabled === false && config?.push_channel_enabled === false && config?.bell_channel_enabled === false && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-xs text-amber-800">
            <strong>⚠️ Tous les canaux sont désactivés.</strong> Tu ne recevras AUCUNE notification
            (ni email, ni push, ni cloche).
          </div>
        )}
      </div>

      {/* ── 2. Email destinataire ─────────────────────────────────────────── */}
      <div className="bg-white border rounded-xl p-5 space-y-4">
        <h3 className="font-semibold text-sm flex items-center gap-2"><Mail className="w-4 h-4" /> Email destinataire & envoi</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-sm font-medium mb-1">Email cabinet</label>
            <input type="email" value={config.cabinet_email || ""} onChange={(e) => update("cabinet_email", e.target.value)}
              placeholder="contact@cabinet.ca" className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="notif-cabinet-email-input" />
            <p className="text-[10px] text-gray-400 mt-0.5">Source actuelle : <span className="font-medium">{config.cabinet_email_source || "env"}</span></p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Envoi par courriel</label>
            <button onClick={() => update("email_enabled", !config.email_enabled)}
              className={`w-full border rounded-lg px-3 py-2 text-sm flex items-center justify-between transition ${
                config.email_enabled ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "bg-gray-50 text-gray-500"
              }`} data-testid="notif-email-toggle">
              <span>{config.email_enabled ? "Activé" : "Désactivé"}</span>
              {config.email_enabled ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
            </button>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              Copie cachée cabinet auto
              <span className="block text-[10px] font-normal text-slate-400">BCC systématique sur docs/signatures</span>
            </label>
            <button onClick={() => update("cabinet_auto_notify", !config.cabinet_auto_notify)}
              className={`w-full border rounded-lg px-3 py-2 text-sm flex items-center justify-between transition ${
                config.cabinet_auto_notify ? "bg-amber-50 border-amber-300 text-amber-700" : "bg-gray-50 text-gray-500"
              }`} data-testid="notif-auto-cabinet-toggle">
              <span>{config.cabinet_auto_notify ? "Activée" : "Désactivée"}</span>
              {config.cabinet_auto_notify ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <button onClick={handleSendEmailTest} disabled={testingEmail || !config.cabinet_email}
          className="border border-emerald-300 text-emerald-700 hover:bg-emerald-50 px-4 py-2 rounded-lg text-sm disabled:opacity-50 flex items-center gap-2"
          data-testid="notif-test-btn">
          {testingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
          {testingEmail ? "Envoi du test..." : "Tester l'envoi"}
        </button>
      </div>

      {/* ── Relance auto des documents à signer (Batch #14.263) ──────────── */}
      <SignatureRemindersCard value={config.signature_reminders} onChange={(v) => update("signature_reminders", v)} />

      {/* ── Tâches : rappels d'échéance par défaut + résumé périodique (Batch #14.286) ── */}
      <TaskAlertsCard />

      {/* ── 3. Push status serveur ────────────────────────────────────────── */}
      <div className="bg-white border rounded-xl p-5 space-y-3" data-testid="push-status-card">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-semibold text-sm flex items-center gap-2"><Activity className="w-4 h-4" /> Notifications push (navigateur)</h3>
          <div className="flex items-center gap-2">
            <span className={`text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1 font-medium ${
              pushActive ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-rose-50 text-rose-700 border border-rose-200"
            }`} data-testid="push-status-badge">
              {pushActive ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
              {pushActive ? "Actif" : "Inactif"}
            </span>
            <button onClick={refresh} disabled={refreshing}
              className="text-xs px-2 py-0.5 rounded-md border border-gray-200 hover:bg-gray-50 inline-flex items-center gap-1 disabled:opacity-50"
              title="Vérifier le statut maintenant" data-testid="push-status-refresh">
              <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
              {refreshing ? "..." : "Actualiser"}
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
          <Stat title="Firebase" value={push.firebase_initialized ? "Initialisé" : "Non initialisé"} testid="push-firebase" />
          <Stat title="Clé FCM" value={push.fcm_key_present ? "Configurée" : "Manquante"} testid="push-fcm-key" />
          <Stat title="Appareils admin" value={push.tokens?.admin ?? 0} testid="push-tokens-admin" />
          <Stat title="Appareils client" value={push.tokens?.client ?? 0} testid="push-tokens-client" />
        </div>
        {!push.firebase_initialized && creds && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs space-y-1" data-testid="push-creds-diagnostic">
            <div className="font-semibold text-amber-900">Diagnostic FIREBASE_CREDENTIALS_B64</div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-amber-800">
              <div>Variable présente :</div>
              <div className="font-mono">{creds.service_account_b64_present ? "Oui" : "Non"}</div>
              <div>Longueur :</div>
              <div className="font-mono">{creds.service_account_b64_length ?? 0} caractères {creds.service_account_b64_length > 0 && creds.service_account_b64_length < 1000 ? "(trop court — devrait être ~3500+)" : ""}</div>
            </div>
          </div>
        )}
      </div>

      {/* ── 4. Cet appareil ─────────────────────────────────────────────── */}
      <div className="bg-white border rounded-xl p-5" data-testid="browser-status-card">
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"><BellRing className="w-4 h-4" /> Cet appareil</h3>
        {!browserStatus && <div className="text-xs text-gray-400">Chargement...</div>}
        {browserStatus && (
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-gray-600">Supporté par le navigateur</span>
              <span className={browserStatus.supported ? "text-emerald-600" : "text-gray-500"}>{browserStatus.supported ? "Oui" : "Non"}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Configuration présente</span>
              <span className={browserStatus.hasConfig ? "text-emerald-600" : "text-amber-600"}>{browserStatus.hasConfig ? "Oui" : "Non (REACT_APP_FIREBASE_*)"}</span></div>
            <div className="flex justify-between items-center"><span className="text-gray-600">Permission</span>
              <PermBadge permission={browserStatus.permission} /></div>
            {browserStatus.supported && browserStatus.hasConfig && (
              <button onClick={requestBrowserPermission} disabled={browserStatus.permission === "denied"}
                className="bg-indigo-600 text-white px-3 py-1.5 rounded-lg text-xs flex items-center gap-1 hover:bg-indigo-700 mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="push-request-permission">
                <BellRing className="w-3.5 h-3.5" />
                {browserStatus.permission === "granted" ? "Enregistrer ce navigateur (token Firebase)"
                  : browserStatus.permission === "denied" ? "Permission refusée — débloquez dans le navigateur"
                  : "Activer les notifications sur cet appareil"}
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── 5. ÉVÉNEMENTS — 3 toggles par event (Email + Push + Cloche) ──── */}
      <div className="bg-white border rounded-xl p-5 space-y-4" data-testid="event-prefs-card">
        <div>
          <h3 className="font-semibold text-sm flex items-center gap-2"><Bell className="w-4 h-4" /> Événements — par canal</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Pour chaque événement, choisis indépendamment <span className="font-bold">📧 Email</span>, <span className="font-bold">📱 Push</span> et <span className="font-bold">🔔 Cloche</span> (dans l&apos;application).
            Tout désactiver = aucune notification pour cet événement.
          </p>
        </div>

        {catalog.map((group) => (
          <EventGroup key={group.id}
            group={group}
            events={events}
            onSet={setEventChannel}
            onBulk={bulkSetGroup}
          />
        ))}
      </div>

      {/* ── 6. Tests d'envoi ────────────────────────────────────────────── */}
      <div className="bg-white border rounded-xl p-5 space-y-4" data-testid="push-tests-card">
        <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><Send className="w-4 h-4" /> Tests d&apos;envoi</h3>
        <div>
          <p className="text-xs text-gray-500 mb-2">Envoie un push à TOUS les appareils admin enregistrés.</p>
          <button onClick={() => runPushTest()} disabled={testingPush}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2 hover:bg-blue-700 disabled:opacity-50"
            data-testid="push-test-admin-btn">
            {testingPush ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Tester admin
          </button>
        </div>
        <div className="border-t pt-4">
          <p className="text-xs text-gray-500 mb-2">Envoie un push à UN client précis (admin → client).</p>
          <div className="flex gap-2 items-center">
            <select value={selectedClient} onChange={e => setSelectedClient(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm flex-1" data-testid="push-test-client-select">
              <option value="">— Choisir un client —</option>
              {clients.map(c => (<option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>))}
            </select>
            <button onClick={() => runPushTest(selectedClient)} disabled={testingPush || !selectedClient}
              className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2 hover:bg-emerald-700 disabled:opacity-50"
              data-testid="push-test-client-btn">
              {testingPush ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Tester client
            </button>
          </div>
        </div>
        {lastTest && (
          <div className={`border rounded-lg p-3 text-sm ${
            lastTest.ok && !lastTest.fallback_used ? "bg-emerald-50 border-emerald-200 text-emerald-800"
              : lastTest.fallback_used ? "bg-amber-50 border-amber-200 text-amber-800"
              : "bg-red-50 border-red-200 text-red-700"
          }`} data-testid="push-test-result">
            <p className="font-medium">{lastTest.message || lastTest.error || "Réponse"}</p>
          </div>
        )}
      </div>

      {/* ── BARRE FLOTTANTE STICKY — Modifications non enregistrées ───── */}
      {dirty && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-slate-900 text-white shadow-2xl rounded-xl px-5 py-3 flex items-center gap-3 border border-slate-700 max-w-2xl w-[calc(100%-2rem)]" data-testid="notif-sticky-save-bar">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold inline-flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400" /> Modifications non enregistrées
            </div>
            <div className="text-[11px] text-slate-300">Vos changements sont en mémoire — cliquez Sauvegarder pour les appliquer.</div>
          </div>
          <button onClick={handleDiscard} disabled={saving}
            className="text-xs px-3 py-1.5 rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-800 disabled:opacity-50"
            data-testid="notif-discard-btn">
            Annuler
          </button>
          <button onClick={handleSave} disabled={saving}
            className="text-sm px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-bold inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="notif-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? "Sauvegarde..." : "Sauvegarder"}
          </button>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Sous-composants
// ════════════════════════════════════════════════════════════════════════════
function Stat({ title, value, testid }) {
  return (
    <div className="bg-gray-50 rounded-lg p-2">
      <p className="text-gray-500">{title}</p>
      <p className="font-semibold" data-testid={testid}>{value}</p>
    </div>
  );
}

function EventGroup({ group, events, onSet, onBulk }) {
  const [collapsed, setCollapsed] = useState(false);
  const groupKeys = (group.events || []).map((e) => e.key);
  // Calcule l'état agrégé du groupe (combien actifs par canal)
  const emailCount = groupKeys.filter((k) => normEv(events[k]).email).length;
  const pushCount = groupKeys.filter((k) => normEv(events[k]).push).length;
  const bellCount = groupKeys.filter((k) => normEv(events[k]).bell).length;
  const total = groupKeys.length;

  return (
    <div className="border rounded-xl overflow-hidden" data-testid={`event-group-${group.id}`}>
      <div role="button" tabIndex={0} onClick={() => setCollapsed((c) => !c)} onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setCollapsed((c) => !c)}
        className="w-full px-3 py-2 bg-slate-50 hover:bg-slate-100 flex items-center justify-between text-left cursor-pointer">
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold text-slate-700 uppercase tracking-wider">{group.label}</div>
          <div className="text-[10px] text-slate-500 mt-0.5">
            <span className="inline-flex items-center gap-1">📧 {emailCount}/{total}</span>
            <span className="mx-2">·</span>
            <span className="inline-flex items-center gap-1">📱 {pushCount}/{total}</span>
            <span className="mx-2">·</span>
            <span className="inline-flex items-center gap-1">🔔 {bellCount}/{total}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <BulkBtn label="📧 Tout" color="emerald" onClick={(e) => { e.stopPropagation(); onBulk(groupKeys, "email", true); }} testid={`bulk-${group.id}-email-on`} />
          <BulkBtn label="📱 Tout" color="indigo" onClick={(e) => { e.stopPropagation(); onBulk(groupKeys, "push", true); }} testid={`bulk-${group.id}-push-on`} />
          <BulkBtn label="🔔 Tout" color="amber" onClick={(e) => { e.stopPropagation(); onBulk(groupKeys, "bell", true); }} testid={`bulk-${group.id}-bell-on`} />
          <BulkBtn label="❌ Rien" color="rose" onClick={(e) => { e.stopPropagation(); onBulk(groupKeys, "all", false); }} testid={`bulk-${group.id}-off`} />
          <span className="text-slate-500 text-xs">{collapsed ? "▸" : "▾"}</span>
        </div>
      </div>
      {!collapsed && (
        <div className="divide-y">
          {group.events?.map((ev) => {
            const pref = normEv(events[ev.key]);
            return (
              <div key={ev.key} className="px-3 py-2 flex items-center gap-3 hover:bg-slate-50" data-testid={`event-row-${ev.key}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-800">{ev.label}</div>
                  {ev.desc && <div className="text-[10px] text-slate-500 truncate">{ev.desc}</div>}
                </div>
                <ChannelToggle channel="email" emoji="📧" label="Email" active={pref.email}
                  onClick={() => onSet(ev.key, "email", !pref.email)} testid={`event-${ev.key}-email`} />
                <ChannelToggle channel="push" emoji="📱" label="Push" active={pref.push}
                  onClick={() => onSet(ev.key, "push", !pref.push)} testid={`event-${ev.key}-push`} />
                <ChannelToggle channel="bell" emoji="🔔" label="Cloche" active={pref.bell}
                  onClick={() => onSet(ev.key, "bell", !pref.bell)} testid={`event-${ev.key}-bell`} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function BulkBtn({ label, color, onClick, testid }) {
  const cls = {
    emerald: "text-emerald-700 hover:bg-emerald-100",
    indigo: "text-indigo-700 hover:bg-indigo-100",
    amber: "text-amber-700 hover:bg-amber-100",
    rose: "text-rose-700 hover:bg-rose-100",
  }[color];
  return (
    <button type="button" onClick={onClick} className={`text-[10px] px-2 py-0.5 rounded ${cls}`} data-testid={testid}>
      {label}
    </button>
  );
}

function ChannelToggle({ emoji, label, active, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} title={`${label} ${active ? "activé" : "désactivé"}`}
      className={`text-xs px-2.5 py-1 rounded-lg border transition inline-flex items-center gap-1.5 min-w-[64px] justify-center ${
        active
          ? "bg-emerald-50 border-emerald-300 text-emerald-700 font-semibold"
          : "bg-slate-50 border-slate-200 text-slate-400"
      }`}
      data-testid={testid}>
      <span>{emoji}</span>
      <span>{active ? "ON" : "OFF"}</span>
    </button>
  );
}
